import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TechniciansModal.css';

const TechniciansModal = ({ isOpen, onClose, onTechnicianSelect }) => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    specializations: [],
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadTechnicians();
    }
  }, [isOpen]);

  const loadTechnicians = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/technicians', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTechnicians(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando técnicos:', error);
      alert('Error al cargar técnicos: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSpecializationToggle = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      alert('Nombre y teléfono son requeridos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingTechnician) {
        await axios.put(`/api/technicians/${editingTechnician._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/technicians', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowForm(false);
      setEditingTechnician(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        specializations: [],
        notes: ''
      });
      loadTechnicians();
    } catch (error) {
      console.error('Error guardando técnico:', error);
      alert('Error al guardar técnico: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (technician) => {
    setEditingTechnician(technician);
    setFormData({
      name: technician.name,
      phone: technician.phone,
      email: technician.email || '',
      specializations: technician.specializations || [],
      notes: technician.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (technicianId) => {
    if (!window.confirm('¿Estás seguro de eliminar este técnico?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/technicians/${technicianId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadTechnicians();
    } catch (error) {
      console.error('Error eliminando técnico:', error);
      alert('Error al eliminar técnico: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSelect = (technician) => {
    if (onTechnicianSelect) {
      onTechnicianSelect(technician);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="technicians-modal-overlay" onClick={onClose}>
      <div className="technicians-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Gestionar Técnicos</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-content">
          <div className="technicians-header">
            <button
              className="btn-add-technician"
              onClick={() => {
                setEditingTechnician(null);
                setFormData({
                  name: '',
                  phone: '',
                  email: '',
                  specializations: [],
                  notes: ''
                });
                setShowForm(true);
              }}
            >
              ➕ Nuevo Técnico
            </button>
          </div>

          {showForm && (
            <div className="technician-form">
              <h3>{editingTechnician ? 'Editar Técnico' : 'Nuevo Técnico'}</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Teléfono *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Especializaciones</label>
                  <div className="specializations">
                    {['instalacion', 'averia', 'mantenimiento', 'otro'].map(spec => (
                      <label key={spec} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.specializations.includes(spec)}
                          onChange={() => handleSpecializationToggle(spec)}
                        />
                        <span>
                          {spec === 'instalacion' ? 'Instalación' :
                           spec === 'averia' ? 'Avería' :
                           spec === 'mantenimiento' ? 'Mantenimiento' : 'Otro'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Notas</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => {
                    setShowForm(false);
                    setEditingTechnician(null);
                  }}>
                    Cancelar
                  </button>
                  <button type="submit">
                    {editingTechnician ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <div className="loading-message">Cargando técnicos...</div>
          ) : (
            <div className="technicians-list">
              {technicians.length === 0 ? (
                <div className="empty-message">No hay técnicos registrados</div>
              ) : (
                technicians.map(technician => (
                  <div key={technician._id} className="technician-item">
                    <div className="technician-info">
                      <div className="technician-name">{technician.name}</div>
                      <div className="technician-details">
                        <span>📞 {technician.phone}</span>
                        {technician.email && <span>📧 {technician.email}</span>}
                        <span className="access-code">🔑 Código: {technician.accessCode || 'Generando...'}</span>
                      </div>
                      {technician.accessCode && (
                        <div className="technician-link">
                          <label>🔗 Link de acceso:</label>
                          <div className="link-container">
                            <code className="access-link">
                              {window.location.origin}/technician
                            </code>
                            <button
                              className="btn-copy-link"
                              onClick={() => {
                                const link = `${window.location.origin}/technician`;
                                navigator.clipboard.writeText(link);
                                alert('Link copiado al portapapeles');
                              }}
                            >
                              📋 Copiar Link
                            </button>
                          </div>
                          <small className="link-instructions">
                            El técnico debe ingresar su código: <strong>{technician.accessCode}</strong>
                          </small>
                        </div>
                      )}
                      {technician.specializations && technician.specializations.length > 0 && (
                        <div className="technician-specializations">
                          {technician.specializations.map(spec => (
                            <span key={spec} className="specialization-badge">
                              {spec === 'instalacion' ? 'Instalación' :
                               spec === 'averia' ? 'Avería' :
                               spec === 'mantenimiento' ? 'Mantenimiento' : 'Otro'}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className={`technician-status ${technician.isActive ? 'active' : 'inactive'}`}>
                        {technician.isActive ? '✅ Activo' : '❌ Inactivo'}
                      </div>
                    </div>
                    <div className="technician-actions">
                      {onTechnicianSelect && (
                        <button
                          className="btn-select"
                          onClick={() => handleSelect(technician)}
                        >
                          Seleccionar
                        </button>
                      )}
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(technician)}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(technician._id)}
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechniciansModal;

